import { useState } from "react";
import { NavLink } from "react-router-dom";
import { useMarque } from "../marque";
import { useModules } from "../modules";
import NousContacter from "./NousContacter";
import {
  IconeClients,
  IconeDeconnexion,
  IconeEnveloppe,
  IconeFermer,
  IconeMenu,
  IconeTableauDeBord,
  IconeAlertes,
  IconeUtilisateurs,
  IconeMonCompte,
  IconeFinancement,
  IconeLocation,
  IconeInspections,
  IconeStock,
  IconeVehicules,
} from "./Icones";

/**
 * Coquille de l'application : navigation laterale + contenu.
 *
 * La barre horizontale precedente convenait a trois liens. Le produit en
 * compte desormais davantage, et en comptera plus encore (journal, reglages,
 * extranet). Une colonne accepte les intitules en toutes lettres, permet de
 * GROUPER par metier, et libere toute la largeur pour le contenu -- une liste
 * de 108 vehicules respire mieux.
 *
 * Aucun ecran n'a disparu : les memes entrees, simplement rangees.
 *
 * MASQUER UN LIEN NE PROTEGE RIEN. Le serveur refuse de toute facon
 * (`exiger_admin`, `exiger_superadmin`). Ce qui suit est du confort : afficher
 * un lien qu'on ne peut pas suivre n'aide personne.
 */

// LES BOUTONS-ICÔNES DU BAS DU MENU (27/10/2026, demande de Yanis) : l'administration et « Mon
// compte » ne sont plus des rubriques, ce sont des boutons EN BAS, à côté de « Nous contacter ».
// Ce sont des écrans qu'on ouvre rarement : ils n'ont pas à occuper le milieu du menu, réservé
// au travail de tous les jours.
//
// L'ADMINISTRATION (Utilisateurs, Réglages) : l'administrateur seulement, comme avant -- le masque
// n'est pas la protection, `exiger_admin` refuse côté serveur.
//
// « MON COMPTE » : POUR TOUT LE MONDE, y compris le travailleur d'atelier et le super-admin, qui
// n'avaient aucun écran pour changer leur mot de passe. Un mot de passe qu'on ne peut pas changer
// soi-même finit par être partagé. Il vient EN DERNIER : c'est le seul bouton que tout le monde
// a, et le dernier emplacement de la rangée.
function liensDuBas(user) {
  const administration =
    user.role === "admin"
      ? [
          { to: "/utilisateurs", label: "Utilisateurs", Icone: IconeUtilisateurs },
          { to: "/reglages", label: "Réglages", Icone: IconeAlertes },
        ]
      : [];
  return [...administration, { to: "/mon-compte", label: "Mon compte", Icone: IconeMonCompte }];
}

function groupes(user, enConsultation, modules) {
  // Chez lui, le super-admin n'a aucune flotte : son organisation ne porte pas
  // de donnees metier, et n'en portera jamais. Il ne voit que ses clients. Les
  // ecrans metier reapparaissent des qu'il entre chez l'un d'eux.
  const estPlateforme = user.role === "superadmin" && !enConsultation;

  // UN MODULE NON SOUSCRIT EST GRISE, PLUS MASQUE (12/08/2026).
  //
  // `verrouille` renvoie vers `/modules/<nom>`, une page STATIQUE qui decrit
  // le module et invite a nous contacter. Elle n'appelle aucune route du
  // module : un ecran verrouille qui interrogerait quand meme le serveur
  // afficherait une erreur au lieu d'une offre.
  //
  // Le renversement est commercial -- un client ne demande pas ce qu'il ne
  // sait pas exister. `exiger_module` continue de refuser cote serveur : le
  // grise n'a jamais ete la protection, et il ne le devient pas.
  const moduleLien = (actif, nom, lien) =>
    actif ? lien : { ...lien, to: `/modules/${nom}`, verrouille: true };

  // LE PÉRIMÈTRE DU MÉCANICIEN (24/09/2026). Il inspecte et gère le stock ; il
  // ne voit ni les coûts ni le financement, donc pas le tableau de bord qui en
  // vit.
  //
  // ⚠️ CE N'EST PAS UN MODULE NON SOUSCRIT, et le traitement diffère : un
  // module qu'on n'a pas reste VISIBLE, grisé, parce qu'un module invisible ne
  // se vend jamais. Ici l'entreprise l'a peut-être acheté — c'est CETTE
  // PERSONNE qui n'y a pas droit. Lui montrer une porte grisée l'inviterait à
  // nous écrire pour acheter ce que son patron possède déjà. On la retire.
  //
  // Le masque n'est PAS la protection : `auth.MODULES_PAR_ROLE` refuse côté
  // serveur, et c'est ce refus qui protège.
  const estMecanicien = user.role === "mecanicien";
  // Le miroir de `auth.MODULES_PAR_ROLE`. Il RESTREINT l'affichage, il
  // n'autorise rien : le serveur refuse de toute facon. Si la table serveur
  // change, celle-ci doit suivre -- sinon le menu propose une porte fermee,
  // ou cache une porte ouverte.
  const MODULES_DU_MECANICIEN = ["vgp", "stock", "operations"];
  const pourLeRole = (nom, lien) =>
    estMecanicien && !MODULES_DU_MECANICIEN.includes(nom) ? null : lien;

  return [
    // LE SOCLE : ce qu'un client a forcement. Son parc, et ce qu'il coute.
    // Tout le reste se vend, et se range en dessous.
    !estPlateforme && {
      titre: "Mon parc",
      liens: [
        // Le mécanicien garde le PARC — sans lui, le sélecteur d'inspection
        // n'a aucun camion à proposer — mais pas le tableau de bord, qui
        // affiche les coûts de la flotte.
        !estMecanicien && {
          to: "/", fin: true, label: "Tableau de bord", Icone: IconeTableauDeBord,
        },
        { to: "/vehicules", label: "Véhicules", Icone: IconeVehicules },
      ].filter(Boolean),
    },
    !estPlateforme && {
      titre: "Modules",
      liens: [
        pourLeRole("vgp", moduleLien(modules.module_vgp, "inspections", {
          to: "/inspections",
          label: "Inspections",
          Icone: IconeInspections,
        })),
        pourLeRole("stock", moduleLien(modules.module_stock, "stock", {
          to: "/stock",
          label: "Stock",
          Icone: IconeStock,
        })),
        // OUVERT A TOUS DEPUIS LE 18/08. La consultation du financement a
        // ete rouverte aux travailleurs sur decision de Yanis ; le lien suit
        // la regle serveur, comme toujours. Un masque plus strict que le
        // serveur est un autre genre de mensonge : l'ecran nierait un acces
        // qui existe.
        //
        // L'ECRITURE, elle, reste reservee aux administrateurs -- c'est le
        // composant de la fiche vehicule qui le gere (`estAdmin`), et le
        // serveur qui l'impose.
        pourLeRole("financement", moduleLien(modules.module_financement, "financement", {
            to: "/financement",
            label: "Financement",
            Icone: IconeFinancement,
          })),
        // Le mecanicien n'y a pas acces : `pourLeRole` le retire, comme le
        // serveur (`MODULES_PAR_ROLE`) le lui refuse. Le travailleur LIT.
        pourLeRole("location", moduleLien(modules.module_location, "location", {
            to: "/locations",
            label: "Locations",
            Icone: IconeLocation,
          })),
      ].filter(Boolean),
    },
    estPlateforme && {
      titre: "Plateforme",
      liens: [{ to: "/plateforme", label: "Clients", Icone: IconeClients }],
    },
  ].filter(Boolean);
}

export default function Layout({ user, onLogout, children }) {
  const marque = useMarque();
  const modules = useModules();
  // JOIGNABLE DEPUIS N'IMPORTE QUEL ECRAN, et pas seulement depuis la page
  // d'un module non souscrit : un client qui a une question ne devrait pas
  // avoir a chercher ou la poser.
  const [contact, setContact] = useState(false);
  const [ouvert, setOuvert] = useState(false); // barre deployee sur mobile

  const consultation = (() => {
    try {
      const brut = localStorage.getItem("consultation");
      return brut ? JSON.parse(brut) : null;
    } catch {
      return null;
    }
  })();

  // Même règle que dans `groupes()` : l'éditeur DANS SON espace, et non pendant
  // une consultation — où il doit voir exactement ce que le client voit.
  // Recalculé ici parce que `groupes()` est une fonction séparée : sa variable
  // du même nom ne franchit pas la frontière du composant.
  const estPlateforme = user.role === "superadmin" && !consultation;

  const fermer = () => setOuvert(false);

  return (
    <div className={`app ${ouvert ? "app-menu-ouvert" : ""}`}>
      <aside className="laterale">
        <div className="laterale-marque">
          {/* Le logo vient de l'organisation, jamais d'un fichier importe dans
              le code : le logiciel est revendu, et chaque client doit voir SA
              marque. Sans logo renseigne, son nom en toutes lettres -- plutot
              qu'un emplacement vide ou, pire, le logo d'un autre client. */}
          {marque.logo_url ? (
            <img src={marque.logo_url} alt={marque.nom} className="laterale-logo" />
          ) : (
            <span className="laterale-nom">{marque.nom}</span>
          )}
          <button
            className="laterale-fermer"
            onClick={fermer}
            aria-label="Fermer le menu"
          >
            <IconeFermer />
          </button>
        </div>

        <nav className="laterale-nav">
          {groupes(user, consultation, modules).map((groupe) => (
            <div key={groupe.titre} className="laterale-groupe">
              <div className="laterale-titre">{groupe.titre}</div>
              {groupe.liens.map(({ to, fin, label, Icone, verrouille }) => (
                <NavLink
                  key={to}
                  to={to}
                  end={fin}
                  onClick={fermer}
                  className={({ isActive }) =>
                    "laterale-lien" +
                    (isActive ? " laterale-lien-actif" : "") +
                    (verrouille ? " laterale-lien-verrouille" : "")
                  }
                  // Un lien grisé RESTE un lien : il mène à la page qui décrit
                  // le module. `aria-disabled` dirait le contraire à un lecteur
                  // d'écran, et l'utilisateur qui en a le plus besoin serait
                  // le seul à ne pas pouvoir la lire.
                  title={verrouille ? `${label} — module non activé` : undefined}
                >
                  <Icone />
                  <span>{label}</span>
                  {verrouille && (
                    <span className="laterale-cadenas" aria-hidden="true">
                      🔒
                    </span>
                  )}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>

        <div className="laterale-bas">
          {/* Ni un lien ni une page : une modale. Ecrire a l'editeur ne doit
              pas faire perdre l'ecran sur lequel on etait -- c'est justement
              son contexte qui nous interesse. */}
          {/* L'ICONE SEULE, SANS LIBELLE (16/09/2026). Le bas du menu empilait
              trois lignes de texte ; celle-ci se lisait le moins, alors qu'une
              enveloppe se reconnait sans etre lue. Le geste ne change pas : un
              clic ouvre la meme modale, avec le meme contexte.

              LE LIBELLE NE DISPARAIT PAS, IL CHANGE DE PLACE : `aria-label` le
              donne au lecteur d'ecran, `title` au survol. Un bouton sans texte
              et sans nom accessible n'est plus qu'un dessin -- et celui qui a
              le plus besoin d'ecrire serait le seul a ne pas savoir ou. */}
          {/* LES BOUTONS-ICÔNES DU BAS : « Nous contacter », l'administration
              (administrateur seulement), puis « Mon compte ». Même cadre, même taille -- et le libellé
              reste en `aria-label` et `title`, comme pour l'enveloppe : un lien
              sans texte et sans nom accessible n'est plus qu'un dessin. Ce sont de
              VRAIS liens (rôle « link », adresse dans la barre) : un clic droit
              ouvre l'écran dans un onglet. */}
          <div className="laterale-actions">
            <button
              className="laterale-deconnexion laterale-contact"
              onClick={() => setContact(true)}
              aria-label="Nous contacter"
              title="Nous contacter"
            >
              <IconeEnveloppe taille={24} />
            </button>
            {liensDuBas(user).map(({ to, label, Icone }) => (
              <NavLink
                key={to}
                to={to}
                onClick={fermer}
                aria-label={label}
                title={label}
                className={({ isActive }) =>
                  "laterale-deconnexion laterale-contact laterale-icone-lien" +
                  (isActive ? " laterale-icone-lien-actif" : "")
                }
              >
                <Icone taille={24} />
              </NavLink>
            ))}
          </div>
          <div className="laterale-user">
            {user.prenom} {user.nom}
          </div>
          <button className="laterale-deconnexion" onClick={onLogout}>
            <IconeDeconnexion />
            <span>Déconnexion</span>
          </button>

          {/* LA SEULE MENTION DE L'ÉDITEUR À L'INTÉRIEUR DE L'APPLICATION.
              Décidée le 17/08/2026, et c'est un arbitrage commercial : le haut
              du menu porte la marque DU CLIENT — c'est son outil — mais un
              utilisateur qui ne peut pas nommer le logiciel ne le recommande
              jamais. Une ligne en petit suffit à le rendre citable.

              Masquée pour l'éditeur dans SON espace : la marque du haut y est
              déjà Ridelle, et « propulsé par Ridelle » sous « Ridelle » n'aurait
              aucun sens. Elle réapparaît pendant une CONSULTATION, et c'est
              voulu — on doit y voir exactement ce que le client voit.

              Pas un lien : un lien depuis l'écran d'un client vers un site
              extérieur ouvre une porte de sortie au milieu de son travail. Le
              bouton « Nous contacter » juste au-dessus est le bon chemin. */}
          {!estPlateforme && (
            <div className="laterale-editeur">
              propulsé par <strong>Ridelle</strong>
            </div>
          )}
        </div>
      </aside>

      {contact && <NousContacter onFermer={() => setContact(false)} />}

      {/* Voile : sur mobile, refermer en touchant a cote du menu. */}
      <div className="laterale-voile" onClick={fermer} />

      <div className="zone-principale">
        {/* Bandeau permanent pendant une consultation. Il ne protege rien -- le
            serveur refuse deja toute ecriture -- mais personne ne doit pouvoir
            oublier qu'il regarde les donnees d'un client. */}
        {consultation && (
          <div className="bandeau-consultation">
            Vous consultez <strong>{consultation.nom}</strong> en lecture seule.
            <button
              className="lien-consultation"
              onClick={() => window.dispatchEvent(new Event("quitter-consultation"))}
            >
              Quitter
            </button>
          </div>
        )}

        {/* Barre etroite visible sur mobile seulement : ouvre la laterale. */}
        <header className="barre-mobile">
          <button
            className="barre-mobile-menu"
            onClick={() => setOuvert(true)}
            aria-label="Ouvrir le menu"
          >
            <IconeMenu taille={22} />
          </button>
          <span className="barre-mobile-nom">{marque.nom}</span>
        </header>

        <main className="contenu">{children}</main>
      </div>
    </div>
  );
}
